// components/FutureLookModal.tsx
import { useState, useEffect, useRef } from 'react';
import {
  X,
  Camera,
  Upload,
  Loader2,
  Sparkles,
  RotateCcw,
  Check,
  SwitchCamera,
  Image as ImageIcon,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Share2,
  History,
  Trash2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface FutureLookModalProps {
  onClose: () => void;
  hasExistingTransformation: boolean;
}

type ModalStep = 'intro' | 'camera' | 'processing' | 'result' | 'history';

interface Transformation {
  id: string;
  original_image_url: string;
  transformed_image_url: string;
  created_at: string;
  transformation_details: {
    improvements?: string[];
  } | null;
}

export default function FutureLookModal({
  onClose,
  hasExistingTransformation,
}: FutureLookModalProps) {
  const [step, setStep] = useState<ModalStep>(hasExistingTransformation ? 'result' : 'intro');
  const [isClosing, setIsClosing] = useState(false);
  const [currentTransformation, setCurrentTransformation] = useState<Transformation | null>(null);
  const [allTransformations, setAllTransformations] = useState<Transformation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Camera state
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Comparison state
  const [comparisonMode, setComparisonMode] = useState<'slider' | 'toggle'>('slider');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showAfter, setShowAfter] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    loadTransformations();
    checkCameras();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const checkCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      setHasMultipleCameras(videoInputs.length > 1);
    } catch (err) {
      console.error('Error checking cameras:', err);
    }
  };

  const loadTransformations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get current protocol
      const { data: protocol } = await supabase
        .from('user_protocols')
        .select('id, generation_date')
        .eq('user_id', user.id)
        .maybeSingle();

      if (protocol?.generation_date) {
        // Get current transformation
        const { data: current } = await supabase
          .from('future_look_transformations')
          .select('*')
          .eq('user_id', user.id)
          .eq('protocol_id', protocol.id)
          .eq('protocol_generation_date', protocol.generation_date)
          .eq('generation_status', 'completed')
          .maybeSingle();

        if (current) {
          setCurrentTransformation(current);
        }
      }

      // Get all transformations for history
      const { data: all } = await supabase
        .from('future_look_transformations')
        .select('*')
        .eq('user_id', user.id)
        .eq('generation_status', 'completed')
        .order('created_at', { ascending: false });

      if (all) {
        setAllTransformations(all);
      }
    } catch (err) {
      console.error('Error loading transformations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const startCamera = async () => {
    setStep('camera');
    setCameraError(null);
    
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        await videoRef.current.play();
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please enable permissions or upload a photo.');
      } else {
        setCameraError('Unable to access camera. Please try uploading a photo instead.');
      }
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;

    const offsetX = (video.videoWidth - size) / 2;
    const offsetY = (video.videoHeight - size) / 2;

    if (facingMode === 'user') {
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, offsetX, offsetY, size, size, 0, 0, size, size);

    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);

    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const size = Math.min(img.width, img.height);
        const outputSize = Math.min(size, 1280);
        canvas.width = outputSize;
        canvas.height = outputSize;

        const offsetX = (img.width - size) / 2;
        const offsetY = (img.height - size) / 2;

        ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, outputSize, outputSize);
        setCapturedImage(canvas.toDataURL('image/jpeg', 0.9));
        setStep('camera');
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const retakePhoto = async () => {
    setCapturedImage(null);
    await startCamera();
  };

  const generateTransformation = async () => {
    if (!capturedImage) return;

    setStep('processing');
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get protocol data
      const { data: protocol } = await supabase
        .from('user_protocols')
        .select('id, generation_date, protocol_data')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!protocol?.generation_date) {
        throw new Error('No protocol found');
      }

      // Get user profile for age/gender
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('chronological_age, gender')
        .eq('user_id', user.id)
        .maybeSingle();

      // Build protocol context from protocol_data
      const protocolData = protocol.protocol_data || {};
      const protocolContext = {
        current_weight: protocolData.current_weight || protocolData.startWeight,
        target_weight: protocolData.target_weight || protocolData.goalWeight,
        weight_change: (protocolData.target_weight || protocolData.goalWeight || 0) - 
                       (protocolData.current_weight || protocolData.startWeight || 0),
        duration_weeks: protocolData.duration_weeks || 21 / 7, // 21 days = 3 weeks
        diet_type: protocolData.diet_plan?.type || protocolData.dietType,
        exercise_intensity: protocolData.exercise_intensity || protocolData.fitnessLevel,
        sleep_improvement: protocolData.sleep_optimization || false,
        stress_reduction: protocolData.stress_management || false,
        hydration_focus: protocolData.hydration || true,
        skin_care_included: protocolData.skin_care || false,
        age: profile?.chronological_age,
        gender: profile?.gender,
        health_goals: protocolData.goals || [],
      };

      // Clean base64
      const cleanBase64 = capturedImage.includes(',') 
        ? capturedImage.split(',')[1] 
        : capturedImage;

      // Get session for auth
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call edge function using fetch directly
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://bhbpnhnndqjzdcblmzky.supabase.co';
      const fetchResponse = await fetch(
        `${supabaseUrl}/functions/v1/generate-future-look`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            protocol_id: protocol.id,
            protocol_generation_date: protocol.generation_date,
            image_base64: cleanBase64,
            protocol_context: protocolContext,
          }),
        }
      );

      const response = await fetchResponse.json();

      if (!fetchResponse.ok) {
        throw new Error(response.error || 'Failed to generate');
      }

      if (!response.success) {
        throw new Error(response.error || 'Generation failed');
      }

      setCurrentTransformation(response.transformation);
      setStep('result');
      loadTransformations(); // Refresh list

    } catch (err: any) {
      console.error('Generation error:', err);
      setError(err.message || 'Failed to generate transformation');
      setStep('intro');
    }
  };

  const toggleCamera = async () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    // Camera will restart with new facing mode
    setTimeout(startCamera, 100);
  };

  // Slider handlers
  const handleSliderMove = (clientX: number) => {
    if (!sliderRef.current || !isDragging.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, position)));
  };

  const downloadImage = async () => {
    if (!currentTransformation) return;
    
    try {
      const response = await fetch(currentTransformation.transformed_image_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `future-look-${Date.now()}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const renderIntro = () => (
    <div className="px-5 pb-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-fuchsia-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-10 h-10 text-violet-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">How Would I Look?</h2>
        <p className="text-sm text-gray-500 mt-2">
          See a preview of yourself after following your 21-day protocol
        </p>
      </div>

      {/* Features */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <Camera className="w-4 h-4 text-violet-600" />
          </div>
          <p className="text-sm text-gray-700">Take or upload a selfie</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-fuchsia-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-fuchsia-600" />
          </div>
          <p className="text-sm text-gray-700">AI generates your transformation</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <Check className="w-4 h-4 text-purple-600" />
          </div>
          <p className="text-sm text-gray-700">Compare before & after</p>
        </div>
      </div>

      {/* Notice */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          This feature can only be used once per protocol. Generate a new protocol to use it again.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
        >
          <Upload size={18} />
          Upload Photo
        </button>
        <button
          onClick={startCamera}
          className="flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm bg-violet-600 hover:bg-violet-700 text-white transition-all"
        >
          <Camera size={18} />
          Take Selfie
        </button>
      </div>

      {/* History Link */}
      {allTransformations.length > 0 && (
        <button
          onClick={() => setStep('history')}
          className="w-full mt-4 flex items-center justify-center gap-2 py-3 text-sm text-gray-500 hover:text-gray-700"
        >
          <History size={16} />
          View Previous Transformations ({allTransformations.length})
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );

  const renderCamera = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
        {capturedImage ? (
          <img src={capturedImage} alt="Captured" className="max-w-full max-h-full object-contain" />
        ) : cameraError ? (
          <div className="text-center p-6">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-white/80 text-sm mb-4">{cameraError}</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-white text-black px-6 py-3 rounded-xl font-bold text-sm"
            >
              Upload Photo Instead
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`max-w-full max-h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />
            {/* Face guide */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-white/30 rounded-full" />
            </div>
          </>
        )}
      </div>

      {/* Controls */}
      <div className="bg-black p-6">
        {capturedImage ? (
          <div className="flex justify-center gap-6">
            <button onClick={retakePhoto} className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <RotateCcw className="text-white" size={24} />
              </div>
              <span className="text-white/80 text-xs">Retake</span>
            </button>
            <button onClick={generateTransformation} className="flex flex-col items-center gap-2">
              <div className="w-20 h-20 rounded-full bg-violet-500 flex items-center justify-center">
                <Sparkles className="text-white" size={32} />
              </div>
              <span className="text-white font-bold text-xs">Generate</span>
            </button>
          </div>
        ) : !cameraError ? (
          <div className="flex justify-center items-center gap-6">
            <button onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <ImageIcon className="text-white" size={24} />
              </div>
              <span className="text-white/80 text-xs">Gallery</span>
            </button>
            <button onClick={capturePhoto} className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
              <div className="w-16 h-16 rounded-full border-4 border-black/20" />
            </button>
            {hasMultipleCameras && (
              <button onClick={toggleCamera} className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                  <SwitchCamera className="text-white" size={24} />
                </div>
                <span className="text-white/80 text-xs">Flip</span>
              </button>
            )}
          </div>
        ) : null}
      </div>

      <canvas ref={canvasRef} className="hidden" />
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
    </div>
  );

  const renderProcessing = () => (
    <div className="px-5 py-12 text-center">
      <div className="w-24 h-24 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Loader2 className="w-12 h-12 text-violet-600 animate-spin" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Creating Your Transformation</h2>
      <p className="text-sm text-gray-500 mb-8">
        Our AI is working its magic. This usually takes 30-60 seconds...
      </p>
      <div className="w-48 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
        <div className="h-full bg-violet-600 rounded-full animate-pulse" style={{ width: '60%' }} />
      </div>
    </div>
  );

  const renderResult = () => {
    if (!currentTransformation) {
      return (
        <div className="px-5 py-12 text-center">
          <p className="text-gray-500">No transformation found</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Mode Toggle */}
        <div className="flex justify-center gap-2 px-4 py-3">
          <button
            onClick={() => setComparisonMode('slider')}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              comparisonMode === 'slider' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Slider
          </button>
          <button
            onClick={() => setComparisonMode('toggle')}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              comparisonMode === 'toggle' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Toggle
          </button>
        </div>

        {/* Comparison View */}
        <div className="flex-1 px-4 flex items-center justify-center">
          {comparisonMode === 'slider' ? (
            <div
              ref={sliderRef}
              className="relative w-full max-w-sm aspect-square cursor-ew-resize select-none rounded-2xl overflow-hidden"
              onMouseDown={() => isDragging.current = true}
              onMouseUp={() => isDragging.current = false}
              onMouseLeave={() => isDragging.current = false}
              onMouseMove={(e) => handleSliderMove(e.clientX)}
              onTouchStart={() => isDragging.current = true}
              onTouchEnd={() => isDragging.current = false}
              onTouchMove={(e) => handleSliderMove(e.touches[0].clientX)}
            >
              {/* After Image (Full) */}
              <img
                src={currentTransformation.transformed_image_url}
                alt="After"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Before Image (Clipped) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={currentTransformation.original_image_url}
                  alt="Before"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Slider Line */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
                  <ChevronLeft className="text-gray-600 -mr-1" size={14} />
                  <ChevronRight className="text-gray-600 -ml-1" size={14} />
                </div>
              </div>
              {/* Labels */}
              <div className="absolute bottom-3 left-3 bg-white/90 text-black px-2 py-1 rounded-full text-xs font-bold">
                BEFORE
              </div>
              <div className="absolute bottom-3 right-3 bg-violet-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                AFTER
              </div>
            </div>
          ) : (
            <div className="relative w-full max-w-sm">
              <img
                src={showAfter ? currentTransformation.transformed_image_url : currentTransformation.original_image_url}
                alt={showAfter ? 'After' : 'Before'}
                className="w-full aspect-square object-cover rounded-2xl"
              />
              <div className={`absolute bottom-3 ${showAfter ? 'right-3' : 'left-3'} ${
                showAfter ? 'bg-violet-600 text-white' : 'bg-white/90 text-black'
              } px-2 py-1 rounded-full text-xs font-bold`}>
                {showAfter ? 'AFTER' : 'BEFORE'}
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={() => setShowAfter(false)}
                  className={`px-6 py-2.5 rounded-xl font-bold text-sm ${!showAfter ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  Before
                </button>
                <button
                  onClick={() => setShowAfter(true)}
                  className={`px-6 py-2.5 rounded-xl font-bold text-sm ${showAfter ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  After
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex gap-3">
            <button
              onClick={downloadImage}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm bg-gray-900 text-white"
            >
              <Download size={18} />
              Save Image
            </button>
            {allTransformations.length > 1 && (
              <button
                onClick={() => setStep('history')}
                className="w-12 bg-gray-100 rounded-xl flex items-center justify-center"
              >
                <History size={20} className="text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="px-5 pb-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Previous Transformations</h2>
      {allTransformations.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <ImageIcon className="text-gray-400" size={28} />
          </div>
          <p className="text-gray-500 text-sm">No transformations yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {allTransformations.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setCurrentTransformation(t);
                setStep('result');
              }}
              className="w-full bg-gray-50 hover:bg-gray-100 rounded-xl p-3 flex items-center gap-3 transition-colors"
            >
              <div className="relative">
                <img src={t.original_image_url} alt="Before" className="w-14 h-14 rounded-lg object-cover" />
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-md overflow-hidden border-2 border-white">
                  <img src={t.transformed_image_url} alt="After" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <p className="font-bold text-sm text-gray-900">
                  {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
                <p className="text-xs text-gray-500">
                  {t.transformation_details?.improvements?.slice(0, 2).join(', ') || 'Transformation'}
                </p>
              </div>
              <ChevronRight className="text-gray-400" size={20} />
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setStep(currentTransformation ? 'result' : 'intro')}
        className="w-full mt-4 py-3 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Back
      </button>
    </div>
  );

  const isFullScreen = step === 'camera';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
        isClosing ? 'bg-black/0' : 'bg-black/50'
      }`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className={`w-full max-w-lg bg-white transform transition-transform duration-300 ease-out ${
          isClosing ? 'translate-y-full' : 'translate-y-0'
        } ${isFullScreen ? 'h-full' : 'rounded-t-[28px] max-h-[90vh]'}`}
      >
        {/* Handle bar (not on camera) */}
        {!isFullScreen && (
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
        )}

        {/* Close button */}
        <button
          onClick={handleClose}
          className={`absolute ${isFullScreen ? 'top-4' : 'top-4'} right-4 w-8 h-8 ${
            isFullScreen ? 'bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
          } rounded-full flex items-center justify-center z-10`}
        >
          <X size={16} className={isFullScreen ? 'text-white' : 'text-gray-600'} />
        </button>

        {/* Content */}
        <div className={isFullScreen ? 'h-full flex flex-col' : ''}>
          {isLoading && step !== 'processing' ? (
            <div className="px-5 py-12 text-center">
              <Loader2 className="w-8 h-8 text-violet-600 animate-spin mx-auto" />
            </div>
          ) : error ? (
            <div className="px-5 py-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={() => { setError(null); setStep('intro'); }}
                className="px-6 py-2 bg-gray-900 text-white rounded-xl font-bold text-sm"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {step === 'intro' && renderIntro()}
              {step === 'camera' && renderCamera()}
              {step === 'processing' && renderProcessing()}
              {step === 'result' && renderResult()}
              {step === 'history' && renderHistory()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}